import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { TCProductComponentStandardInformation } from "../../../../tc-models/tc-req-dto/tc-component-details.req_dto";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import useNotification from "../../../../../../shared/hooks/useNotification";

interface ProductComponentProps {
  productInfoData: TCProductComponentStandardInformation[];
  setProductInfoData: React.Dispatch<React.SetStateAction<TCProductComponentStandardInformation[]>>;
  /** Total certified allocated weight (kg) from Raw Material step */
  certifiedAllocatedWeight?: number;
}

// Define Yup validation schema for each row
const validationSchema = Yup.object().shape({
  tcComponentStandardLabelGrade: Yup.string().nullable(),
  tcComponentCertifiedWeight: Yup.string()
    .test(
      'is-exactly-five-decimal-places',
      'Number must be a valid decimal number with upto 5 decimal places.',
      (value: string | undefined) => {
        if (!value) return true; // Allow empty values for required validation to handle
        return /^(0|[1-9]\d*|0\.\d{1,5}|[1-9]\d*\.\d{1,5})$/.test(value); // Match up to five decimal places
      }
    )
    .typeError("Certified Weight must be a valid number"),
}).test(
  'at-least-one',
  'At least one field is required',
  function (values) {
    return values.tcComponentStandardLabelGrade || values.tcComponentCertifiedWeight ? true : this.createError({ path: 'field1', message: 'At least one field is required' });
  }
);;

const ProductComponent: React.FC<ProductComponentProps> = ({
  productInfoData,
  setProductInfoData,
  certifiedAllocatedWeight = 0,
}) => {
  const [data, setData] = useState<TCProductComponentStandardInformation[]>(
    productInfoData
  ); // Initialize with prop data
  const [tableData, setTableData] = useState<TCProductComponentStandardInformation[]>(
    productInfoData
  ); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { updateField } = useFormActions("component-details");
  const { notify } = useNotification();
  const [labelGrades, setLabelGrades] = useState([]);

  const { isRestrictPopUpSave, setRestrictPopUpSave, getLabelGrades } = useTransactionCertificateStore();

  useEffect(() => {
    setLabelGrades(getLabelGrades);
  }, [])
  const formik = useFormik<TCProductComponentStandardInformation>({
    initialValues: {
      tcComponentStandardLabelOrder: "",
      tcComponentStandardLabelGrade: "",
      tcComponentCertifiedWeight: '',
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  useEffect(() => {
    data.forEach((item: any, index) => {
      item.tcComponentStandardLabelOrder = index + 1;
    });
    updateField("standard-info", data)
  }, [data])

  const handleAddNewRow = () => {
    const newRow: TCProductComponentStandardInformation = {
      tcComponentStandardLabelOrder: "",
      tcComponentStandardLabelGrade: "",
      tcComponentCertifiedWeight: '',
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
   setData(tableData);
    // Reset editing state and form
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    if (editingRowIndex === -1) return;

    const selectedGrade = formik.values.tcComponentStandardLabelGrade || "";
    const weight = formik.values.tcComponentCertifiedWeight || "";
    
    // Validate decimal places for weight
    if (weight && !/^(0|[1-9]\d*|0\.\d{1,5}|[1-9]\d*\.\d{1,5})$/.test(weight)) {
      notify(
        "Certified Weight must have up to 5 decimal places only",
        "error"
      );
      return;
    }

    if (selectedGrade) {
      const baseStandard = selectedGrade.substring(0, 3);
      const conflict = data.some((row, idx) => {
        if (idx === editingRowIndex) return false;
        const grade = row.tcComponentStandardLabelGrade || "";
        if (!grade) return false;
        return grade.substring(0, 3) === baseStandard && grade !== selectedGrade;
      });
      if (conflict) {
        notify(
          "Two different Standard Label Grades from the same Standard cannot be selected.",
          "error"
        );
        return;
      }
    }

    
    const proposedTotalCertifiedWeight = data.reduce((sum, row, idx) => {
      if (idx === editingRowIndex) {
        return sum + Number(weight || 0);
      }
      const rowWeight = Number(row.tcComponentCertifiedWeight || 0);
      return sum + (Number.isNaN(rowWeight) ? 0 : rowWeight);
    }, 0);
    
    const allowedCertifiedWeight = Number(certifiedAllocatedWeight || 0);
    const tolerance = 1e-6;
    if (proposedTotalCertifiedWeight > allowedCertifiedWeight + tolerance) {
      notify(
        `Total Certified Weight cannot exceed Certified Weight Identified (${allowedCertifiedWeight.toFixed(5)} Kg). Current total: ${proposedTotalCertifiedWeight.toFixed(5)} Kg.`,
        "error"
      );
      return;
    }

    if (!formik.isValid) return;

    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);
    setProductInfoData(newData); // Update parent with current data
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const columns: EditableColumnDef<TCProductComponentStandardInformation>[] = [
    {
      accessorKey: "tcComponentStandardLabelGrade",
      header: "Standard Label Grade",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Standard Label Grade
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          label=""
          name="tcComponentStandardLabelGrade"
          placeholder="Select standard label grade"
          value={value}
          enableSearch={true}
          options={labelGrades}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
    {
      accessorKey: "tcComponentCertifiedWeight",
      header: "Certified Weight (kg)",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Certified Weight (kg)
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
            name="tcComponentCertifiedWeight"
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            placeholder="Enter value"
            sx={{ mb: 2 }}
          />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setProductInfoData(updatedData); // Update parent with updated data
  };

  return (
    <div>
      <Box>
        <Typography fontWeight={400}>
          Certified Weight Identified: {Number(certifiedAllocatedWeight || 0).toFixed(5)} Kg
        </Typography>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton
            onClick={handleAddNewRow}
            disabled={isRestrictPopUpSave || !formik.isValid}
            sx={{ mb: 2 }}
          >
            Add Product Component Standard Information
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default ProductComponent;
